import axios, { type AxiosInstance, type AxiosResponse, type AxiosProgressEvent } from 'axios'
import { useToast } from 'vue-toastification'
import { config } from '@/config'
import type {
  User,
  LoginCredentials,
  RegisterData,
  UpdateUserData,
  ChangePasswordData,
  AuthResponse,
  ApiResponse,
  PaginationParams,
  PaginatedResponse,
  UserStats
} from '@/types'

class ApiService {
  public api: AxiosInstance
  private toast = useToast()

  constructor() {
    this.api = axios.create({
      baseURL: config.api.baseURL,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('auth_token')
          localStorage.removeItem('auth_user')
          window.location.href = '/login'
        } else if (error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/register')) {
          // Don't show toast for auth errors, let the component handle them
        } else if (error.response?.data?.error || error.response?.data?.message) {
          this.toast.error(error.response.data.error || error.response.data.message)
        } else if (error.message) {
          this.toast.error(error.message)
        }
        return Promise.reject(error)
      }
    )
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/admin/auth/login', credentials)
    return response.data.data!
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/admin/auth/register', data)
    return response.data.data!
  }

  async logout(): Promise<void> {
    await this.api.post('/admin/auth/logout')
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/admin/auth/me')
    return response.data.data!
  }

  async refreshToken(): Promise<{ token: string; expiresAt: string }> {
    const response = await this.api.post<ApiResponse<{ token: string; expiresAt: string }>>('/admin/auth/refresh')
    return response.data.data!
  }

  // User endpoints
  async getUsers(params?: PaginationParams): Promise<PaginatedResponse<User>> {
    const response = await this.api.get<ApiResponse<PaginatedResponse<User>>>('/admin/users', { params })
    return response.data.data!
  }

  async getUser(id: number): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>(`/admin/users/${id}`)
    return response.data.data!
  }

  async createUser(data: RegisterData): Promise<User> {
    const response = await this.api.post<ApiResponse<User>>('/admin/users', data)
    return response.data.data!
  }

  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await this.api.put<ApiResponse<User>>(`/admin/users/${id}`, data)
    return response.data.data!
  }

  async changePassword(id: number, data: ChangePasswordData): Promise<void> {
    await this.api.put(`/admin/users/${id}/password`, data)
  }

  async deleteUser(id: number): Promise<void> {
    await this.api.delete(`/admin/users/${id}`)
  }

  async getUserStats(): Promise<UserStats> {
    const response = await this.api.get<ApiResponse<UserStats>>('/admin/users/stats/overview')
    return response.data.data!
  }

  // Upload file (sin timeout de cliente + reintentos automaticos ante fallos de red, util para conexiones lentas)
  async uploadFile(
    file: File,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
    maxRetries: number = 3
  ): Promise<{ url: string }> {
    let attempt = 0
    while (true) {
      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await this.api.post<ApiResponse<{ url: string }>>('/admin/upload', formData, {
          headers: { 'Content-Type': null as any },
          timeout: 0,
          onUploadProgress
        })
        return response.data.data!
      } catch (error: any) {
        const isNetworkError = !error.response
        attempt++
        if (!isNetworkError || attempt > maxRetries) {
          throw error
        }
        const backoffMs = Math.min(1000 * 2 ** (attempt - 1), 8000)
        await new Promise((resolve) => setTimeout(resolve, backoffMs))
      }
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.api.get('/health-check')
    return response.data
  }

  // Paginated APIs
  async getHealthPaginated(params?: any): Promise<any> {
    const response = await this.api.get('/admin/health', { params })
    return response.data
  }

  async getNutritionPaginated(params?: any): Promise<any> {
    const response = await this.api.get('/admin/nutrition', { params })
    return response.data
  }

  async getEducationPaginated(params?: any): Promise<any> {
    const response = await this.api.get('/admin/education', { params })
    return response.data
  }
}

export const apiService = new ApiService()
export default apiService